import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { Context, RenderContext, SortOrder } from './experimental.grid';
import { ColumnType } from './grid.insight.model';
import { TranslationKeyMap } from './insight.i18n';
import { CriteriaIdType } from './performance-data/metric.pd.model';

@Component({
    selector: 'default-grid',
    template: `<ng-template #tpl><div></div></ng-template> `,
})
export class DefaultGridComponent {
    @ViewChild('tpl') tpl: TemplateRef<any>;
}

@Component({
    selector: 'default-row',
    template: `
        <ng-template #tpl>
            <div class="insight-default"></div>
        </ng-template>
    `,
})
export class DefaultRowComponent {
    @ViewChild('tpl') tpl: TemplateRef<any>;
}

@Component({
    selector: 'default-cell',
    template: `
        <ng-template
            let-ctx="ctx"
            #tpl
        >
            <div class="insight-default">{{ ctx.value }}</div>
        </ng-template>
    `,
})
export class DefaultCellComponent {
    @ViewChild('tpl') tpl: TemplateRef<any>;
}

@Component({
    selector: 'expand-pillar',
    template: ` <div class="expand-pillar-wrapper">
        <button
            type="button"
            class="btn bg-transparent p-0"
            tabIndex="-1"
            (click)="btnClick.emit()"
        >
            <cmps-icon
                class="cmps-cdk-fill-grey-100"
                [svgIcon]="!expanded ? 'chevron-left' : 'chevron-up'"
            ></cmps-icon>
        </button>
    </div>`,
})
export class ExpandPillarComponent implements OnDestroy {
    @Input() expanded: boolean;
    @Output() btnClick: EventEmitter<void>;

    constructor() {
        this.btnClick = new EventEmitter();
    }

    ngOnDestroy(): void {
        this.btnClick.complete();
    }
}

@Component({
    selector: 'expand-metric',
    template: ` <div class="expand-metric-wrapper">
        <button
            type="button"
            class="btn bg-transparent p-0"
            tabIndex="-1"
            (click)="btnClick.emit()"
        >
            <cmps-icon
                class="cmps-cdk-fill-grey-100"
                [svgIcon]="
                    !expanded ? 'unfold-more-vertical' : 'unfold-less-vertical'
                "
            ></cmps-icon>
        </button>
    </div>`,
})
export class ExpandMetricComponent implements OnDestroy {
    @Input() expanded: boolean;
    @Output() btnClick: EventEmitter<void>;

    constructor() {
        this.btnClick = new EventEmitter();
    }

    ngOnDestroy(): void {
        this.btnClick.complete();
    }
}

@Component({
    selector: 'column-element',
    template: ` <ng-template
        let-g="g"
        let-c="c"
        let-ctx="ctx"
        #tpl
    >
        <div
            [class.insight-pillar-bg]="
                ctx.type === _ColumnType.PILLAR ||
                (ctx.type === _ColumnType.SUB_PILLAR && c.id.includes('-STUB-'))
            "
            [class.insight-sub-pillar-bg]="
                ctx.type === _ColumnType.SUB_PILLAR && !c.id.includes('-STUB-')
            "
            [class.insight-metric-bg]="ctx.type === _ColumnType.METRIC"
            [class.insight-component-metric-bg]="
                ctx.type === _ColumnType.COMPONENT_METRIC
            "
        >
            <div
                class="insight-column insight-pillar"
                *ngIf="ctx.type === _ColumnType.PILLAR"
            >
                <span class="label">{{ ctx.name }}</span>
            </div>
            <div
                class="insight-column insight-sub-pillar"
                *ngIf="ctx.type === _ColumnType.SUB_PILLAR"
            >
                <expand-pillar
                    [expanded]="c.ctx.pillarExpanded"
                    (btnClick)="g.context.ctx.refs.onExpandPillarClick(c)"
                ></expand-pillar>
                <div class="label-wrapper">
                    <span class="label">{{ ctx.name }}</span>
                </div>
            </div>
            <div
                class="insight-column insight-metric"
                *ngIf="ctx.type === _ColumnType.METRIC"
            >
                <expand-pillar
                    [expanded]="c.ctx.pillarExpanded"
                    (btnClick)="g.context.ctx.refs.onExpandPillarClick(c)"
                    *ngIf="ctx.hasPillarHandle"
                ></expand-pillar>
                <div class="label-wrapper">
                    <span
                        class="label"
                        [attr.data-metric]=""
                    >
                        {{ ctx.name }}
                    </span>
                </div>
                <expand-metric
                    [expanded]="c.ctx.metricExpanded"
                    (btnClick)="g.context.ctx.refs.onExpandMetricClick(c)"
                    *ngIf="ctx.hasComponents"
                ></expand-metric>
            </div>
            <div
                class="insight-column insight-component-metric"
                *ngIf="ctx.type === _ColumnType.COMPONENT_METRIC"
            >
                <span
                    class="label"
                    [attr.data-component-metric]=""
                >
                    {{ ctx.name }}
                </span>
            </div>
            <div
                class="insight-column insight-optional-column"
                *ngIf="
                    ![
                        _ColumnType.NODE,
                        _ColumnType.PILLAR,
                        _ColumnType.SUB_PILLAR,
                        _ColumnType.METRIC,
                        _ColumnType.COMPONENT_METRIC
                    ].includes(ctx.type)
                "
            >
                <button
                    type="button"
                    class="btn bg-transparent p-0 cmps-cdk-d-flex cmps-cdk-align-items-center"
                    tabIndex="-1"
                    (click)="
                        $event.stopPropagation();
                        g.context.ctx.refs.onSortClick(c)
                    "
                    [attr.data-optional-column]=""
                >
                    <span>{{ ctx.name }}</span>
                    <cmps-icon
                        class="cmps-cdk-fill-grey-300"
                        svgIcon="chevron-down"
                        *ngIf="
                            g.context.ctx.sort?.def?.columnId === c.id &&
                            g.context.ctx.sort.def.order === _SortOrder.DESC
                        "
                    ></cmps-icon>
                    <cmps-icon
                        class="cmps-cdk-fill-grey-300"
                        svgIcon="chevron-up"
                        *ngIf="
                            g.context.ctx.sort?.def?.columnId === c.id &&
                            g.context.ctx.sort.def.order === _SortOrder.ASC
                        "
                    ></cmps-icon>
                </button>
            </div>
        </div>
    </ng-template>`,
})
export class ColumnElementComponent {
    @ViewChild('tpl') tpl: TemplateRef<any>;

    /** @private */
    _ColumnType = ColumnType;

    /** @private */
    _SortOrder = SortOrder;
}

@Component({
    selector: 'node-element',
    template: ` <ng-template
        let-g="g"
        let-r="r"
        let-ctx="ctx"
        #tpl
    >
        <div class="w-100">
            <div
                class="cmps-cdk-pr-0dot5 insight-node"
                [class.insight-has-next-level]="r.context.ctx.hasNextLevel"
                [style.padding-left]="
                    'calc(0.5rem + (1.8rem * ' + r.context.ctx.depth + '))'
                "
                (click)="
                    r.context.ctx.hasNextLevel
                        ? g.context.ctx.refs.onExpandRowClick(r)
                        : false
                "
            >
                <button
                    type="button"
                    class="btn bg-transparent p-0 mr-2"
                    tabIndex="-1"
                    *ngIf="r.context.ctx.hasNextLevel"
                >
                    <cmps-icon
                        class="cmps-cdk-fill-grey-300"
                        svgIcon="progress-helper"
                        *ngIf="r.context.ctx.pending"
                    ></cmps-icon>
                    <cmps-icon
                        class="cmps-cdk-fill-grey-300"
                        svgIcon="chevron-down"
                        *ngIf="!r.context.ctx.pending && r.context.ctx.expanded"
                    ></cmps-icon>
                    <cmps-icon
                        class="cmps-cdk-fill-grey-300"
                        svgIcon="chevron-right"
                        *ngIf="
                            !r.context.ctx.pending && !r.context.ctx.expanded
                        "
                    ></cmps-icon>
                </button>
                <div
                    class="btn bg-transparent p-0 mr-2 invisible"
                    aria-hidden="true"
                    *ngIf="!r.context.ctx.hasNextLevel"
                >
                    <div class="insight-handle"></div>
                </div>
                <ng-container
                    *ngIf="
                        g.context.ctx &&
                        [
                            _CriteriaIdType.USER_ID,
                            _CriteriaIdType.TEAM_ID,
                            _CriteriaIdType.SUPER_GROUP_ID
                        ].includes(
                            r.context.ctx.levelDataMap.displayNameCriteriaIdType
                        )
                    "
                >
                    <cmps-icon
                        class="cmps-cdk-fill-grey-300 cmps-cdk-mr-0dot5"
                        svgIcon="account-circle"
                        *ngIf="
                            r.context.ctx.levelDataMap
                                .displayNameCriteriaIdType ===
                                _CriteriaIdType.USER_ID ||
                            r.context.ctx.levelDataMap
                                .displayNameCriteriaIdType ===
                                _CriteriaIdType.TEAM_ID
                        "
                    ></cmps-icon>
                    <cmps-icon
                        class="cmps-cdk-fill-blue-100 cmps-cdk-mr-0dot5"
                        svgIcon="account-multiple"
                        *ngIf="
                            r.context.ctx.levelDataMap
                                .displayNameCriteriaIdType ===
                            _CriteriaIdType.SUPER_GROUP_ID
                        "
                    ></cmps-icon>
                </ng-container>
                <span class="mr-auto">
                    <ng-container *ngIf="ctx.value.name">{{
                        ctx.value.name | translate
                    }}</ng-container>
                </span>
                <!-- r.context.ctx.depth !== 0 -> not root node -->
                <ng-container
                    *ngIf="
                        r.context.ctx.depth !== 0 && ctx.value.notCurrentManager
                    "
                >
                    <span class="mr-2 ml-2 text-danger">*</span>
                </ng-container>
                <button
                    type="button"
                    class="btn bg-transparent p-0 ml-2"
                    tabIndex="-1"
                    (click)="
                        $event.stopPropagation();
                        g.context.ctx.refs.onNewTabClick(r)
                    "
                    *ngIf="r.context.ctx.allowNewTab"
                >
                    <cmps-icon
                        class="cmps-cdk-fill-grey-300 hover:cmps-cdk-fill-blue-100"
                        svgIcon="open-in-new"
                    ></cmps-icon>
                </button>
                <ng-container
                    *ngIf="
                        r.context.ctx.hasSuperGroup === false ||
                        r.context.ctx.hasQueue === false
                    "
                >
                    <div
                        [cmpsTooltipTrigger]="
                            (r.context.ctx.hasSuperGroup === false
                                ? _i18n._11081_THIS_QUEUE_IS_NOT_PART_OF_THE_SUPERGROUP
                                : _i18n._11082_THIS_SUPERGROUP_IS_NOT_ASSOCIATED_TO_THE_QUEUE
                            ) | translate
                        "
                    >
                        <cmps-icon
                            class="cmps-cdk-fill-red-100"
                            svgIcon="exclamation-thick"
                        ></cmps-icon>
                    </div>
                </ng-container>

                <button
                    type="button"
                    class="btn bg-transparent p-0 ml-2"
                    tabIndex="-1"
                    (click)="
                        $event.stopPropagation();
                        g.context.ctx.refs.onToggleSelectionItemClick(r)
                    "
                    *ngIf="
                        g.context.ctx?.trendVisible && !ctx.value.unSelectable
                    "
                >
                    <cmps-icon
                        [class.cmps-cdk-fill-blue-100]="r.context.ctx.selected"
                        [class.cmps-cdk-fill-grey-300]="!r.context.ctx.selected"
                        svgIcon="check-bold"
                    ></cmps-icon>
                </button>
            </div>
        </div>
    </ng-template>`,
})
export class NodeElementComponent {
    @ViewChild('tpl', { static: true }) tpl: TemplateRef<any>;

    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _CriteriaIdType = CriteriaIdType;
}

@Component({
    selector: 'metric-element',
    template: ` <ng-template
        let-g="g"
        let-c="c"
        let-ctx="ctx"
        #tpl
    >
        <div>
            <div class="insight-node-metric">
                <ng-container *ngIf="c.ctx.type === _ColumnType.CURRENT_ACTUAL">
                    <span
                        [class.insight-metric-bold]="
                            g.context.ctx.showThresholdColours &&
                            ctx.value.metric.targetThresholdBold
                        "
                        [style.color]="
                            g.context.ctx.showThresholdColours &&
                            ctx.value.metric.targetThresholdColor
                        "
                    >
                        {{
                            ctx.value.metric.actual == null
                                ? '-'
                                : (ctx.value.metric.actual
                                  | cmpsNumber: '1.2-2')
                        }}
                    </span>
                    <span
                        class="sr-only"
                        *ngIf="g.context.ctx.showThresholdColours"
                    >
                        {{ ctx.value.metric.targetThresholdName }}
                    </span>
                    <ng-container
                        *ngIf="
                            g.context.ctx.showTrendArrows &&
                            ctx.value.metric.trendClass?.split(' ')?.length > 1
                        "
                    >
                        <cmps-icon
                            [style.color]="
                                ctx.value.metric.trendClass.split(' ')[0]
                            "
                            svgIcon="chevron-up"
                            *ngIf="
                                ctx.value.metric.trendClass
                                    .split(' ')[1]
                                    .toLowerCase()
                                    .includes('up')
                            "
                        ></cmps-icon>
                        <cmps-icon
                            [style.color]="
                                ctx.value.metric.trendClass.split(' ')[0]
                            "
                            svgIcon="chevron-down"
                            *ngIf="
                                ctx.value.metric.trendClass
                                    .split(' ')[1]
                                    .toLowerCase()
                                    .includes('down')
                            "
                        ></cmps-icon>
                        <span class="sr-only">
                            {{ ctx.value.metric.trendName }}
                        </span>
                    </ng-container>
                </ng-container>
                <span *ngIf="c.ctx.type === _ColumnType.PROJECTED_RESULT">
                    {{
                        ctx.value.metric.projectedActual == null
                            ? '-'
                            : (ctx.value.metric.projectedActual
                              | cmpsNumber: '1.2-2')
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.PERIOD_TARGET">
                    {{
                        ctx.value.metric.projectedTarget == null
                            ? '-'
                            : (ctx.value.metric.projectedTarget
                              | cmpsNumber: '1.2-2')
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.GAP">
                    {{
                        ctx.value.metric.gap == null
                            ? '-'
                            : (ctx.value.metric.gap | cmpsNumber: '1.2-2')
                    }}
                </span>
                <span
                    *ngIf="
                        c.ctx.type === _ColumnType.PROJECTED_PERCENT_TO_TARGET
                    "
                >
                    {{
                        ctx.value.metric.projectedPctToTgt == null
                            ? '-'
                            : (ctx.value.metric.projectedPctToTgt
                              | cmpsPercent: '1.2-2')
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.PREVIOUS_ACTUAL">
                    {{
                        ctx.value.metric.previousActual == null
                            ? '-'
                            : (ctx.value.metric.previousActual
                              | cmpsNumber: '1.2-2')
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.CURRENT_TARGET">
                    {{
                        ctx.value.metric.target == null
                            ? '-'
                            : (ctx.value.metric.target | cmpsNumber: '1.2-2')
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.GOAL">
                    {{
                        ctx.value.metric.goal == null
                            ? '-'
                            : (ctx.value.metric.goal | cmpsNumber: '1.2-2')
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.PERCENT_TO_TARGET">
                    {{
                        ctx.value.metric.percentToTarget == null
                            ? '-'
                            : (ctx.value.metric.percentToTarget
                              | cmpsPercent: '1.2-2')
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.PEER_GROUP_ACTUAL">
                    {{
                        !ctx.value.peerGroup ||
                        ctx.value.peerGroup.actual == null
                            ? '-'
                            : (ctx.value.peerGroup.actual | cmpsNumber: '1.2-2')
                    }}
                </span>
                <span
                    *ngIf="
                        c.ctx.type === _ColumnType.PEER_GROUP_PERCENT_TO_TARGET
                    "
                >
                    {{
                        !ctx.value.peerGroup ||
                        ctx.value.peerGroup.percentToTarget == null
                            ? '-'
                            : (ctx.value.peerGroup.percentToTarget
                              | cmpsPercent: '1.2-2')
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.OBJECTIVE_MET">
                    {{
                        ctx.value.metric.objectiveMet == null
                            ? '-'
                            : !ctx.value.metric.objectiveMet
                            ? 'N'
                            : 'Y'
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.WEIGHT">
                    {{
                        ctx.value.metric.weight == null
                            ? '-'
                            : (ctx.value.metric.weight | cmpsNumber: '1.2-2')
                    }}
                </span>
                <span *ngIf="c.ctx.type === _ColumnType.WEIGHTED_SCORE">
                    {{
                        ctx.value.metric.weightedScore == null
                            ? '-'
                            : (ctx.value.metric.weightedScore
                              | cmpsNumber: '1.2-2')
                    }}
                </span>
            </div>
        </div>
    </ng-template>`,
})
export class MetricElementComponent {
    @ViewChild('tpl', { static: true }) tpl: TemplateRef<any>;

    /** @private */
    _ColumnType = ColumnType;
}

export const render = (context: Context): RenderContext => {
    let tpl = context.ctx ? context.ctx.tpl : null;
    if (!tpl) {
        if (context.t === 'grid') {
            tpl = context.g.context.ctx.defaultGridTpl;
        }
        if (context.t === 'row') {
            tpl = context.g.context.ctx.defaultRowTpl;
        }
        if (context.t === 'cell') {
            tpl = context.g.context.ctx.defaultCellTpl;
        }
    }
    if (!tpl) return;
    const ref = tpl.createEmbeddedView(context);
    return {
        element: ref.rootNodes[0],
        detectChanges: () => ref.detectChanges(),
        destroy: () => ref.destroy(),
    };
};
